/* Minimal i18n: dictionary-based translations for English + Telugu.
 * Telugu translation kept conservative: only commonly-visible UI labels translated.
 */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const STRINGS = {
  en: {
    'nav.home': 'Home',
    'nav.categories': 'Categories',
    'nav.compare': 'Compare',
    'nav.account': 'My Account',
    'nav.search': 'Search',
    'nav.sell': 'Sell on TelanganaMart',
    'nav.register': 'Register',
    'nav.signin': 'Sign In',
    'nav.shopping': 'TelanganaMart Shopping',
    'nav.help': 'Help',
    'cta.post_requirement': 'Post Your Requirement',
    'cta.become_vendor': 'Become a Vendor',
    'cta.contact': 'Contact',
    'search.placeholder': 'Search products, vendors, services…',
    'search.button': 'Search',
    'home.tagline': "Telangana's Largest B2B Marketplace",
    'home.headline': 'Get the Best Quotes for your Business',
    'home.sub': 'Tell us what you need and get verified vendors ready to quote within minutes.',
    'home.tell_us': 'Tell us what you need',
    'home.verified_vendors': 'Verified Vendors',
    'home.best_quotes': 'Best Quotes',
    'home.support': '24/7 Support',
    'home.delivery': 'Pan Telangana Delivery',
    'home.shop_by_dept': 'Search by Category',
    'home.top_categories': 'Top Categories',
    'compare.title': 'Compare Products',
    'account.title': 'My Account',
    'common.loading': 'Loading…',
    'common.no_results': 'No results found.',
    'lang.label': 'Language',
    'theme.dark': 'Dark Mode',
    'theme.light': 'Light Mode',
  },
  te: {
    'nav.home': 'హోమ్',
    'nav.categories': 'వర్గాలు',
    'nav.compare': 'పోల్చండి',
    'nav.account': 'నా ఖాతా',
    'nav.search': 'వెతకండి',
    'nav.sell': 'తెలంగాణమార్ట్‌లో అమ్మండి',
    'nav.register': 'నమోదు',
    'nav.signin': 'సైన్ ఇన్',
    'nav.shopping': 'తెలంగాణమార్ట్ షాపింగ్',
    'nav.help': 'సహాయం',
    'cta.post_requirement': 'మీ అవసరం పోస్ట్ చేయండి',
    'cta.become_vendor': 'విక్రేత అవ్వండి',
    'cta.contact': 'సంప్రదించండి',
    'search.placeholder': 'ఉత్పత్తులు, విక్రేతలు, సేవలు వెతకండి…',
    'search.button': 'వెతకండి',
    'home.tagline': 'తెలంగాణ యొక్క అతిపెద్ద B2B మార్కెట్‌ప్లేస్',
    'home.headline': 'మీ వ్యాపారానికి ఉత్తమ కోట్‌లను పొందండి',
    'home.sub': 'మీకు ఏమి కావాలో చెప్పండి — నిర్ధారిత విక్రేతలు నిమిషాల్లో కోట్ చేస్తారు.',
    'home.tell_us': 'మీకు ఏమి కావాలో చెప్పండి',
    'home.verified_vendors': 'నిర్ధారిత విక్రేతలు',
    'home.best_quotes': 'ఉత్తమ కోట్‌లు',
    'home.support': '24/7 సహాయం',
    'home.delivery': 'తెలంగాణ అంతటా డెలివరీ',
    'home.shop_by_dept': 'వర్గం ద్వారా శోధించండి',
    'home.top_categories': 'ముఖ్య వర్గాలు',
    'compare.title': 'ఉత్పత్తులు పోల్చండి',
    'account.title': 'నా ఖాతా',
    'common.loading': 'లోడ్ అవుతోంది…',
    'common.no_results': 'ఫలితాలు ఏవీ లేవు.',
    'lang.label': 'భాష',
    'theme.dark': 'డార్క్ మోడ్',
    'theme.light': 'లైట్ మోడ్',
  },
};

const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem('tgmart_lang') || 'en'; } catch { return 'en'; }
  });
  useEffect(() => {
    try { localStorage.setItem('tgmart_lang', lang); } catch (err) { console.warn('I18nContext: localStorage write failed', err); }
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const t = useCallback((key) => (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key, [lang]);
  const value = useMemo(() => ({ lang, setLang: setLangState, t }), [lang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
